import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
  timeout: 30000
});

// Retries automáticos para erros de rede puros (sem response — timeout, blip de wifi, deploy
// reiniciando o backend momentaneamente). Sem isso, qualquer instabilidade transitória cai direto
// no toastError de cada tela como "Network Error" cru, sem tentar de novo — daí a issue #114
// (erros de rede exibidos corriqueiramente, mesmo sem problema real de conectividade). Restrito a
// GET: métodos que alteram estado (POST/PUT/DELETE) não são retentados automaticamente, pois a
// falha pode ter ocorrido depois do servidor já ter processado a requisição.
const NETWORK_RETRY_MAX = 2;
const NETWORK_RETRY_DELAY_MS = 800;

const isPureNetworkError = error =>
  !error.response && (error.code === "ERR_NETWORK" || error.code === "ECONNABORTED" || error.message === "Network Error");

let isRefreshing = false;
let failedQueue = [];
let lastRefreshTime = Date.now();
const REFRESH_THRESHOLD = 25 * 60 * 1000; 

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleLogout = () => {
  api.delete("/auth/logout").catch(() => { });
  localStorage.removeItem("token");
  window.location.href = "/login";
};

let refreshPromise = null;

const refreshToken = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const { data: { token, user } } = await api.post("/auth/refresh_token");
      localStorage.setItem("token", JSON.stringify(token));
      api.defaults.headers.Authorization = `Bearer ${token}`;
      lastRefreshTime = Date.now();
      return { token, user };
    } catch (err) {
      if (err?.response?.data?.error === "ERR_USER_INACTIVE") {
        throw err;
      }
      handleLogout();
      throw err;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

let userInactive = false;

const setUserInactiveFlag = (value) => {
  userInactive = value;
};

api.interceptors.request.use(
  async config => {
    if (userInactive && !config.url.includes("/auth/logout")) {
      return Promise.reject(new Error("User is inactive"));
    }

    if (config.url.includes("/auth/refresh_token") || config.url.includes("/auth/logout")) {
      return config;
    }

    const skipProactiveRefresh = config.url.includes("/error-logs");

    const token = localStorage.getItem("token");
    if (token) {
      const now = Date.now();
      if (!skipProactiveRefresh && now - lastRefreshTime > REFRESH_THRESHOLD) {
        try {
          const { token: newToken } = await refreshToken();
          config.headers.Authorization = `Bearer ${newToken}`;
        } catch (err) {
          if (err?.response?.data?.error === "ERR_USER_INACTIVE") {
            setUserInactiveFlag(true);
          }
          console.error("Error refreshing token:", err);
          return Promise.reject(err);
        }
      } else {
        try {
          const parsedToken = JSON.parse(token);
          config.headers.Authorization = `Bearer ${parsedToken}`;
        } catch (err) {
          console.error("Erro ao processar token:", err);
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error?.response?.data?.error === "ERR_SESSION_EXPIRED" || error?.response?.data?.error === "ERR_USER_INACTIVE") {
      if (error?.response?.data?.error === "ERR_USER_INACTIVE") {
        return Promise.reject(error);
      }

      handleLogout();
      return Promise.reject(error);
    }

    if (error?.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes("/auth/login")) {
      if (isRefreshing) {
        try {
          const token = await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { token } = await refreshToken();
        processQueue(null, token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    if (
      isPureNetworkError(error) &&
      originalRequest &&
      (originalRequest.method || "get").toLowerCase() === "get"
    ) {
      const retryCount = originalRequest._networkRetryCount || 0;
      if (retryCount < NETWORK_RETRY_MAX) {
        originalRequest._networkRetryCount = retryCount + 1;
        await new Promise(resolve => setTimeout(resolve, NETWORK_RETRY_DELAY_MS));
        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

export const markRefreshTime = () => {
  lastRefreshTime = Date.now();
};

export const markUserAsInactive = () => {
  setUserInactiveFlag(true);
};

export default api;